import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiEye } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../api/axios.js";
import { API_IMAGE_BASE_URL } from "../../config/api";
import "./VendorList.css";

const VendorList = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const location = useLocation();
  const [categoryName, setCategoryName] = useState(location.state?.categoryName || "");
  const [vendors, setVendors] = useState(location.state?.vendors || []);
  const [loading, setLoading] = useState(!location.state?.vendors?.length);

  useEffect(() => {
    if (location.state?.vendors?.length) {
      setVendors(location.state.vendors);
      setCategoryName(location.state.categoryName || "");
      setLoading(false);
      return;
    }

    const fetchVendorsForCategory = async () => {
      if (!categoryId) return;
      try {
        setLoading(true);
        // Same source as Service page — find this category in the paginated list
        let page = 1;
        let found = null;
        let totalPages = 1;

        while (page <= totalPages && !found) {
          const res = await api.get("/listing/vendor-under/categories", {
            params: { page, limit: 50 },
          });
          const list = res?.data?.data || [];
          totalPages = res?.data?.pagination?.totalPages || 1;
          found = list.find((item) => String(item.categoryId) === String(categoryId));
          page += 1;
        }

        if (found) {
          setVendors(found.vendors || []);
          setCategoryName(found.category || "");
        } else {
          setVendors([]);
          toast.error("Vendors for this category were not found. Open the list again from Services.");
        }
      } catch (err) {
        console.error("Failed to load vendors for category", err);
        toast.error(err?.response?.data?.message || "Failed to load vendors");
        setVendors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVendorsForCategory();
  }, [categoryId, location.state]);

  const getVendorId = (vendorItem) =>
    vendorItem?.vendor?._id ||
    (typeof vendorItem?.vendor === "string" ? vendorItem.vendor : null) ||
    vendorItem?.vendorId ||
    null;

  return (
    <div className="vendor-list-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>
        <h2>Vendors in "{categoryName || "Category"}"</h2>
      </div>

      {loading ? (
        <div className="no-vendors">
          <p>Loading vendors...</p>
        </div>
      ) : (
        <div className="vendors-grid">
          {vendors?.map((vendorItem) => {
            const vendor = vendorItem.vendor || {};
            const vendorId = getVendorId(vendorItem);
            return (
              <div key={vendorId || vendorItem._id} className="vendor-card">
                <div className="vendor-image">
                  <img
                    src={
                      vendor?.profile_image
                        ? `${API_IMAGE_BASE_URL}${vendor.profile_image}`
                        : "/uploads/avatardefaultimg.jpeg"
                    }
                    alt={vendor?.name || "Vendor"}
                    onError={(e) => {
                      e.target.src = "/uploads/avatardefaultimg.jpeg";
                    }}
                  />
                </div>
                <div className="vendor-info">
                  <h3>{vendor?.name || "N/A"}</h3>
                  <p className="vendor-email">{vendor?.email || "—"}</p>
                  <p className="vendor-phone">{vendor?.mobile_number || "—"}</p>
                  <p className="vendor-shop">{vendor?.shop_name || "—"}</p>
                  <div className="price-range">
                    <span>
                      ₹{vendorItem.min_price ?? "—"} - ₹{vendorItem.max_price ?? "—"}
                    </span>
                  </div>
                  <button
                    className="view-details-btn"
                    disabled={!vendorId}
                    onClick={() => navigate(`/vendor-details/${vendorId}`)}
                  >
                    <FiEye /> View Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !vendors?.length && (
        <div className="no-vendors">
          <p>No vendors found in this category.</p>
        </div>
      )}
    </div>
  );
};

export default VendorList;
